import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Field, getIn } from 'formik'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'

import styled from 'styled-components'

import ReactSelect, { components } from 'react-select'

import FormGroup from '../FormGroup'

const ClearButton = styled.button`
    white-space: nowrap;
`

const Option = props => {
    const dataProp = {
        innerProps: {
            'data-testid': props.data.label.toLowerCase().replaceAll(' ', '-'),
            ...props.innerProps,
        },
    }
    return <components.Option {...{ ...props, ...dataProp }} />
}

// Adds the data-testid attributes the Cypress tests use to find the selected tags. A component of
// its own so the effect is not called conditionally inside the Field render callback.
const MultiValueTestIds = () => {
    useEffect(() => {
        const temp = document.getElementsByClassName('list__multi-value__label')

        if (temp.length > 0) {
            for (let index = 0; index < temp.length; index++) {
                const t1 = document.getElementsByClassName('list__multi-value__label')[index]
                let t2 = document.createAttribute('data-testid')
                t2.value = `${temp[index].innerHTML
                    .toLowerCase()
                    .replaceAll(' ', '-')}-multiselect-tag`
                t1.setAttributeNode(t2)
            }
        }
    })

    return null
}

const Tags = ({
    name,
    label,
    help,
    tooltip,
    placeholder = false,
    options = [],
    setFieldValue,
    handleChange = () => {},
    className = '',
    showWhen,
    defaultValue = null,
    allowClear = false,
    testId,
    testIdItems,
    required = null,
    ...props
}) => {
    return (
        <Field name={name}>
            {({ field, form }) => {
                let error = getIn(form.errors, name)

                if (error) {
                    const errorFieldName = label ? label : 'This'
                    error = error.replace(field.name, errorFieldName)
                }

                if (showWhen) {
                    const showField = form.values.hasOwnProperty(showWhen)
                        ? form.values[showWhen]
                        : false

                    if (!showField) {
                        return null
                    }
                }

                props.className = classNames(className)

                if (help) {
                    props['aria-describedby'] = `${name}Help`
                }

                if (allowClear) {
                    options.unshift({ label: placeholder || 'Please Select...', value: '' })
                }

                const getTagValues = (values = []) => {
                    let results = []

                    if (typeof values === 'string') {
                        let parsedValues = values
                        if (values[0] === '[') {
                            // If the array is coming through as a string then parse it
                            parsedValues = JSON.parse(values)
                        } else {
                            // avoid breaking backwards compatibility for fields that were a single <select> value
                            parsedValues = [values]
                        }
                        values = parsedValues
                    }

                    if (options && values?.length > 0) {
                        results = values?.map(fieldVal =>
                            options.find(option => option.value === fieldVal)
                        )
                    }

                    return results
                }

                return (
                    <FormGroup {...{ name, label, help, tooltip, error, required }}>
                        <div
                            className="!flex !items-center !justify-between"
                            data-testid={`${label.toLowerCase().replaceAll(' ', '-')}-tags`}
                        >
                            <ReactSelect
                                components={{ Option }}
                                className="!w-full"
                                classNamePrefix="list"
                                closeMenuOnSelect={false}
                                isDisabled={form.isSubmitting || props.disabled}
                                name={name}
                                value={getTagValues(field.value)}
                                defaultValue={defaultValue}
                                onChange={option => {
                                    form.setFieldValue(
                                        field.name,
                                        option ? option.map(opt => opt.value) : []
                                    )
                                    handleChange(option ? option.value : [])
                                }}
                                onBlur={field.onBlur}
                                options={options}
                                placeholder={placeholder || 'Please Select...'}
                                isMulti={true}
                            />

                            {allowClear && (
                                <ClearButton
                                    className="btn btn-link"
                                    onClick={e => {
                                        e.preventDefault()
                                        form.setFieldValue(field.name, '')
                                        handleChange('')
                                    }}
                                    data-testId={`clear-${field.name
                                        .toLowerCase()
                                        .replaceAll(' ', '-')}`}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="!mr-2" />
                                    Clear Value
                                </ClearButton>
                            )}
                        </div>
                        <MultiValueTestIds />
                    </FormGroup>
                )
            }}
        </Field>
    )
}

Tags.displayName = 'Tags'

Tags.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    help: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    placeholder: PropTypes.string,
    options: PropTypes.array.isRequired,
    className: PropTypes.string,
}

export default Tags
